import React, { useState, useEffect, useRef, useCallback, useMemo, useLayoutEffect } from 'react'
import { motion } from 'framer-motion'
import { Box, Image, Text, VStack, useColorModeValue } from '@chakra-ui/react'
import { randomInt, getBggGameCoverUrl, getBggGameThumbnailUrl } from '../Utils'

/** Max games in the roll pool (same cap as before). */
export const POOL_CAP = 40

/** Max CSS width of the strip card (matches main `Container` rhythm). */
export const STRIP_MAX_W = 640
const TILE_WIDTH_MAX = 300
const TILE_WIDTH_MIN = 184
/** Landscape 16:9 tile band: image area height = tileW × (9/16), `object-fit: contain` inside. */
export const STRIP_TILE_ASPECT_RATIO = 9 / 16
const TITLE_BAND_MIN_H = 60

export function stripImageBandHeightForTileWidth(tileW) {
	if (tileW <= 0) return Math.round(TILE_WIDTH_MAX * STRIP_TILE_ASPECT_RATIO)
	return Math.round(tileW * STRIP_TILE_ASPECT_RATIO)
}
const WINNER_HOLD_MS = 720
const SPIN_DURATION_S = 4.85
const SPIN_LOOPS = 3
const WINNER_INDEX_MIN = 26
const WINNER_INDEX_MAX = 46
const LEAD_TILE_MIN = 10
const LEAD_TILE_MAX = 20
const TRAIL_TILE_MIN = 20
const TRAIL_TILE_MAX = 36
const EASE_OUT = [0.2, 1, 0.26, 1]

/** Upper bound for strip card body (tallest image band + title) — reserve space / loading shell. */
export const STRIP_CARD_BODY_MAX_H = stripImageBandHeightForTileWidth(TILE_WIDTH_MAX) + TITLE_BAND_MIN_H

/** Min height for loading shell / layout reserve (label + gaps + card inner). */
export function getPickerStripSectionMinHeight() {
	const labelRow = 22
	const vstackGap = 12
	const cardChrome = 6
	return labelRow + vstackGap + cardChrome + STRIP_CARD_BODY_MAX_H
}

function deriveTileWidth(viewportW) {
	if (viewportW <= 0) return TILE_WIDTH_MAX
	return Math.min(TILE_WIDTH_MAX, Math.max(TILE_WIDTH_MIN, Math.round(viewportW * 0.72)))
}

function edgeFadeWidth(viewportW) {
	if (viewportW <= 0) return 72
	return Math.min(100, Math.max(44, Math.round(viewportW * 0.14)))
}

function sameObjectId(a, b) {
	if (!a || !b) return false
	return String(a['@_objectid']) === String(b['@_objectid'])
}

function pickRandomFromPool(rollPool) {
	return rollPool[randomInt(rollPool.length)]
}

function pickRandomNotWinner(rollPool, winner) {
	const n = rollPool.length
	if (n <= 1) return rollPool[0]
	for (let t = 0; t < 20; t++) {
		const g = rollPool[randomInt(n)]
		if (!sameObjectId(g, winner)) return g
	}
	return rollPool.find((g) => !sameObjectId(g, winner)) || rollPool[0]
}

function gameTitle(game) {
	if (!game || !game.name) return ''
	return typeof game.name === 'object' && game.name['#text'] != null ? String(game.name['#text']) : String(game.name)
}

function StripTileImage({ game, imageHeightPx }) {
	const preferred = game ? getBggGameCoverUrl(game) : ''
	const thumb = game ? getBggGameThumbnailUrl(game) : ''
	const [src, setSrc] = useState(() => preferred || '')

	useEffect(() => {
		setSrc(preferred || '')
	}, [game, preferred])

	const onError = useCallback(() => {
		if (src === preferred && thumb && thumb !== preferred) {
			setSrc(thumb)
			return
		}
		setSrc('')
	}, [src, preferred, thumb])

	return (
		<Image
			src={src || undefined}
			alt=""
			w="full"
			h={`${imageHeightPx}px`}
			objectFit="contain"
			bg="gray.100"
			loading="lazy"
			decoding="async"
			onError={onError}
		/>
	)
}

function buildStripPlan(rollPool, winner, viewportW, tileW) {
	const leadCount = LEAD_TILE_MIN + randomInt(LEAD_TILE_MAX - LEAD_TILE_MIN + 1)
	const trailCount = TRAIL_TILE_MIN + randomInt(TRAIL_TILE_MAX - TRAIL_TILE_MIN + 1)

	if (rollPool.length <= 1) {
		const games = []
		for (let i = 0; i < leadCount; i++) {
			games.push(winner)
		}
		games.push(winner)
		for (let i = 0; i < trailCount; i++) {
			games.push(winner)
		}
		const winnerStripIndex = leadCount
		const cycleWidth = tileW
		const xEnd = viewportW / 2 - tileW / 2 - winnerStripIndex * tileW
		const xStart = xEnd + SPIN_LOOPS * cycleWidth
		return { games, xStart, xEnd, winnerStripIndex, tileW }
	}

	const span = WINNER_INDEX_MAX - WINNER_INDEX_MIN + 1
	const decoyCount = WINNER_INDEX_MIN + randomInt(span)
	const games = []
	for (let i = 0; i < leadCount; i++) {
		games.push(pickRandomNotWinner(rollPool, winner))
	}
	for (let i = 0; i < decoyCount; i++) {
		games.push(pickRandomNotWinner(rollPool, winner))
	}
	games.push(winner)
	for (let i = 0; i < trailCount; i++) {
		games.push(pickRandomFromPool(rollPool))
	}

	const winnerStripIndex = leadCount + decoyCount
	const cycleWidth = Math.max(rollPool.length, 1) * tileW
	const xEnd = viewportW / 2 - tileW / 2 - winnerStripIndex * tileW
	const xStart = xEnd + SPIN_LOOPS * cycleWidth
	return { games, xStart, xEnd, winnerStripIndex, tileW }
}

/**
 * Horizontal strip draw: scrolls a row of covers (thumbnail fallback), eases out on the winner, then completes.
 * Contract: onComplete(winner, sessionId).
 */
export default function PickerDrawFlip({ rollPool, winner, sessionId, onComplete }) {
	const [finalPulse, setFinalPulse] = useState(false)
	const [measuredWidth, setMeasuredWidth] = useState(0)
	const holdTimerRef = useRef(null)
	const sessionIdRef = useRef(sessionId)
	const stripSettledRef = useRef(false)
	const viewportRef = useRef(null)

	const border = useColorModeValue('gray.200', 'whiteAlpha.300')
	const bg = useColorModeValue('white', 'bgg.800')
	const muted = useColorModeValue('gray.600', 'gray.400')

	const tileW = useMemo(() => deriveTileWidth(measuredWidth), [measuredWidth])

	const plan = useMemo(() => {
		if (!rollPool || rollPool.length === 0 || measuredWidth <= 0) return null
		return buildStripPlan(rollPool, winner, measuredWidth, tileW)
		// sessionId in deps: new strip geometry every roll
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [rollPool, winner, sessionId, measuredWidth, tileW])

	useLayoutEffect(() => {
		const el = viewportRef.current
		if (!el) return
		const read = () => {
			const w = Math.round(el.getBoundingClientRect().width)
			if (w > 0) setMeasuredWidth(w)
		}
		read()
		const ro = new ResizeObserver(read)
		ro.observe(el)
		return () => ro.disconnect()
	}, [sessionId])

	const clearHold = useCallback(() => {
		if (holdTimerRef.current != null) {
			window.clearTimeout(holdTimerRef.current)
			holdTimerRef.current = null
		}
	}, [])

	useEffect(() => {
		sessionIdRef.current = sessionId
	}, [sessionId])

	useEffect(() => {
		clearHold()
		setFinalPulse(false)
		stripSettledRef.current = false
		if (!rollPool || rollPool.length === 0) {
			onComplete(winner, sessionId)
			return
		}
		return clearHold
	}, [rollPool, winner, sessionId, onComplete, clearHold])

	const onStripRest = useCallback(() => {
		if (sessionIdRef.current !== sessionId) return
		if (stripSettledRef.current) return
		stripSettledRef.current = true
		setFinalPulse(true)
		clearHold()
		holdTimerRef.current = window.setTimeout(() => {
			holdTimerRef.current = null
			if (sessionIdRef.current === sessionId) {
				onComplete(winner, sessionId)
			}
		}, WINNER_HOLD_MS)
	}, [sessionId, winner, onComplete, clearHold])

	if (!rollPool || rollPool.length === 0) {
		return null
	}

	const fadeW = edgeFadeWidth(measuredWidth)
	const stripBodyH = plan ? stripImageBandHeightForTileWidth(plan.tileW) + TITLE_BAND_MIN_H : STRIP_CARD_BODY_MAX_H
	const imageBandPx = plan ? stripImageBandHeightForTileWidth(plan.tileW) : stripImageBandHeightForTileWidth(TILE_WIDTH_MAX)

	return (
		<Box
			w="full"
			maxW={`${STRIP_MAX_W}px`}
			mx="auto"
			alignSelf="center"
			aria-busy="true"
			aria-live="off"
			aria-label="Drawing a random game"
		>
			<VStack spacing={3} align="center" py={2}>
				<Text fontSize="xs" fontWeight="600" color={muted} textTransform="uppercase" letterSpacing="0.08em">
					Drawing
				</Text>
				<motion.div
					key={sessionId}
					animate={
						finalPulse
							? {
									scale: [1, 1.04, 1],
									boxShadow: [
										'0 0 0 0 rgba(237, 100, 42, 0)',
										'0 0 0 10px rgba(237, 100, 42, 0.1)',
										'0 0 0 0 rgba(237, 100, 42, 0)',
									],
							  }
							: { scale: 1 }
					}
					transition={{ duration: finalPulse ? 0.55 : 0.2 }}
					style={{ width: '100%', maxWidth: `min(100%, ${STRIP_MAX_W}px)` }}
				>
					<Box
						ref={viewportRef}
						position="relative"
						w="100%"
						maxW={`${STRIP_MAX_W}px`}
						mx="auto"
						borderWidth="2px"
						borderColor={finalPulse ? 'brand.400' : border}
						rounded="2xl"
						overflow="hidden"
						bg={bg}
						boxShadow="lg"
						minH={plan ? undefined : `${STRIP_CARD_BODY_MAX_H}px`}
					>
						{plan ? (
							<>
								<Box
									position="absolute"
									left={0}
									top={0}
									bottom={0}
									w={`${fadeW}px`}
									zIndex={2}
									pointerEvents="none"
									bgGradient="linear(to-r, whiteAlpha.900, whiteAlpha.0)"
									_dark={{ bgGradient: 'linear(to-r, gray.800, transparent)' }}
								/>
								<Box
									position="absolute"
									right={0}
									top={0}
									bottom={0}
									w={`${fadeW}px`}
									zIndex={2}
									pointerEvents="none"
									bgGradient="linear(to-l, whiteAlpha.900, whiteAlpha.0)"
									_dark={{ bgGradient: 'linear(to-l, gray.800, transparent)' }}
								/>
								<Box
									position="absolute"
									left="50%"
									top={0}
									bottom={0}
									w="4px"
									ml="-2px"
									zIndex={2}
									pointerEvents="none"
									bg="brand.400"
									opacity={0.88}
									boxShadow="0 0 0 1px rgba(0,0,0,0.06)"
								/>

								<Box position="relative" h={`${stripBodyH}px`} overflow="hidden">
									<motion.div
										initial={{ x: plan.xStart }}
										animate={{ x: plan.xEnd }}
										transition={{
											duration: SPIN_DURATION_S,
											ease: EASE_OUT,
										}}
										onAnimationComplete={onStripRest}
										style={{
											display: 'flex',
											flexDirection: 'row',
											width: 'max-content',
											willChange: 'transform',
										}}
									>
										{plan.games.map((g, i) => (
											<Box
												key={`${String(g['@_objectid'])}-${i}`}
												flex={`0 0 ${plan.tileW}px`}
												w={`${plan.tileW}px`}
												borderRightWidth="1px"
												borderColor={border}
												bg={bg}
											>
												<StripTileImage game={g} imageHeightPx={imageBandPx} />
												<Box
													px={3}
													py={2.5}
													borderTopWidth="1px"
													borderColor={border}
													minH={`${TITLE_BAND_MIN_H}px`}
													display="flex"
													alignItems="center"
													justifyContent="center"
												>
													<Text
														fontWeight="600"
														fontSize="sm"
														textAlign="center"
														noOfLines={2}
														lineHeight="short"
														color="gray.800"
														_dark={{ color: 'gray.100' }}
													>
														{gameTitle(g)}
													</Text>
												</Box>
											</Box>
										))}
									</motion.div>
								</Box>
							</>
						) : null}
					</Box>
				</motion.div>
			</VStack>
		</Box>
	)
}
